import React, { useState, useRef, useEffect } from 'react'

interface CopyLinkDropdownProps {
  record: {
    id: string | number
    type?: string
  }
  baseUrl?: string
}

const CopyLinkDropdown: React.FC<CopyLinkDropdownProps> = ({
  record,
  baseUrl = window.location.origin,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [copiedItem, setCopiedItem] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Determine the detail URL based on record type
  const getDetailUrl = () => {
    const type = record.type?.toLowerCase()

    if (type === 'course') {
      return `${baseUrl}/e-course/detail/${record.id}`
    } else if (type === 'live_teaching') {
      return `${baseUrl}/live/detail/${record.id}`
    } else if (type === 'cbt') {
      return `${baseUrl}/ukom/detail/${record.id}`
    }

    // Default fallback - try to determine from current admin path
    const currentPath = window.location.pathname
    if (currentPath.includes('/admin/courses')) {
      return `${baseUrl}/e-course/detail/${record.id}`
    } else if (currentPath.includes('/admin/live-teaching')) {
      return `${baseUrl}/live/detail/${record.id}`
    } else if (currentPath.includes('/admin/cbt')) {
      return `${baseUrl}/ukom/detail/${record.id}`
    }

    return `${baseUrl}/e-course/detail/${record.id}`
  }

  // Checkout URL is the same for all types
  const getCheckoutUrl = () => {
    return `${baseUrl}/course/checkout?id=${record.id}`
  }

  const copyToClipboard = async (url: string, type: 'detail' | 'checkout') => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedItem(type)

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedItem(null)
      }, 2000)
    } catch (err) {
      console.error('Failed to copy: ', err)
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = url
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)

      setCopiedItem(type)
      setTimeout(() => {
        setCopiedItem(null)
      }, 2000)
    }
  }

  const handleDetailCopy = () => {
    const url = getDetailUrl()
    copyToClipboard(url, 'detail')
  }

  const handleCheckoutCopy = () => {
    const url = getCheckoutUrl()
    copyToClipboard(url, 'checkout')
  }

  return (
    <div className="dropdown" ref={dropdownRef}>
      <button
        className="d-inline-flex fs-14 action-icon"
        onClick={() => setIsOpen(!isOpen)}
        title="Copy Links"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <i className="isax isax-link" />
      </button>

      {isOpen && (
        <div
          className="dropdown-menu show"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 1000,
            minWidth: '200px',
            padding: '8px 0',
            backgroundColor: 'white',
            border: '1px solid #dee2e6',
            borderRadius: '0.375rem',
            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
          }}
        >
          <button
            className="dropdown-item d-flex align-items-center"
            onClick={handleDetailCopy}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: '14px',
              color: copiedItem === 'detail' ? '#28a745' : '#212529',
            }}
          >
            <i className="isax isax-eye me-2" />
            {copiedItem === 'detail' ? 'Copied!' : 'Copy Detail Link'}
          </button>

          <button
            className="dropdown-item d-flex align-items-center"
            onClick={handleCheckoutCopy}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: '14px',
              color: copiedItem === 'checkout' ? '#28a745' : '#212529',
            }}
          >
            <i className="isax isax-shopping-cart me-2" />
            {copiedItem === 'checkout' ? 'Copied!' : 'Copy Checkout Link'}
          </button>
        </div>
      )}
    </div>
  )
}

export default CopyLinkDropdown
